import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Printer, CalendarClock, Ticket, FileText, Plus, MapPin, Pencil, Trash2, Phone, Mail, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ClientTimeline from '../components/ClientTimeline';
import BarcodeScannerModal from '../components/BarcodeScannerModal';
import SostituisciCassaDialog from '../components/SostituisciCassaDialog';
import QrAdeDialog from '../components/QrAdeDialog';
import ImpiantiTab from '../components/cliente/ImpiantiTab';
import AbbonamentiTab from '../components/cliente/AbbonamentiTab';
import { TicketsTab, DocumentiTab } from '../components/cliente/TicketsDocumentiTabs';
import { parseApiError } from '../lib/parseApiError';
import { formatDate } from '../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClienteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [cliente, setCliente] = useState(null);
  const [puntiVendita, setPuntiVendita] = useState([]);
  const [impianti, setImpianti] = useState([]);
  const [abbonamenti, setAbbonamenti] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [documenti, setDocumenti] = useState([]);
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [puntoDialogOpen, setPuntoDialogOpen] = useState(false);
  const [impiantoDialogOpen, setImpiantoDialogOpen] = useState(false);
  const [editingPunto, setEditingPunto] = useState(null);
  const [editingImpianto, setEditingImpianto] = useState(null);
  
  const [puntoForm, setPuntoForm] = useState({
    nome: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    referente_nome: '',
    referente_telefono: '',
    referente_email: '',
    note: ''
  });
  
  const [impiantoForm, setImpiantoForm] = useState({
    punto_vendita_id: '',
    tipo: '',
    marca: '',
    modello: '',
    matricola: '',
    data_installazione: '',
    data_scadenza_verifica: '',
    note: ''
  });

  // Barcode scanner (per pre-compilare la matricola/marca/modello dal magazzino)
  const [impiantoScannerOpen, setImpiantoScannerOpen] = useState(false);
  const [scannedArticolo, setScannedArticolo] = useState(null); // articolo trovato in magazzino (per assegnazione post-save)

  // Sostituzione cassa
  const [sostituisciOpen, setSostituisciOpen] = useState(false);
  const [impiantoDaSostituire, setImpiantoDaSostituire] = useState(null);

  // QR Agenzia delle Entrate
  const [qrAdeOpen, setQrAdeOpen] = useState(false);
  const [impiantoQr, setImpiantoQr] = useState(null);

  const openQrAde = (impianto) => {
    setImpiantoQr(impianto);
    setQrAdeOpen(true);
  };

  const openSostituisci = (impianto) => {
    setImpiantoDaSostituire(impianto);
    setSostituisciOpen(true);
  };

  const handleImpiantoScan = async (codice) => {
    try {
      const { data } = await axios.get(`${API}/magazzino/scan/${encodeURIComponent(codice)}`);
      if (data.stato === 'venduto') {
        toast.error(`⚠️ Cassa già assegnata a ${data.venduto_cliente_nome || 'altro cliente'}`);
        return;
      }
      setScannedArticolo(data);
      setImpiantoForm(prev => ({
        ...prev,
        matricola: data.matricola || codice,
        marca: data.marca || prev.marca,
        modello: data.modello || prev.modello,
      }));
      toast.success(`Trovato in magazzino: ${data.descrizione}`);
    } catch (error) {
      const notFound = error?.response?.status === 404;
      if (notFound) {
        // Nessun match: usa solo il codice come matricola
        setImpiantoForm(prev => ({ ...prev, matricola: codice }));
        toast.info(`Matricola "${codice}" inserita (non in magazzino)`);
        setScannedArticolo(null);
      } else {
        toast.error(parseApiError(error, 'Errore ricerca magazzino'));
      }
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [clienteRes, puntiRes, impiantiRes, abbonamentiRes, ticketsRes, documentiRes, contrattiRes] = await Promise.all([
        axios.get(`${API}/clienti/${id}`),
        axios.get(`${API}/punti-vendita?cliente_id=${id}`),
        axios.get(`${API}/impianti?cliente_id=${id}`),
        axios.get(`${API}/abbonamenti?cliente_id=${id}`),
        axios.get(`${API}/tickets?cliente_id=${id}`),
        axios.get(`${API}/documenti?cliente_id=${id}`),
        axios.get(`${API}/contratti?cliente_id=${id}`)
      ]);
      setCliente(clienteRes.data);
      setPuntiVendita(puntiRes.data);
      setImpianti(impiantiRes.data);
      setAbbonamenti(abbonamentiRes.data);
      setTickets(ticketsRes.data);
      setDocumenti(documentiRes.data);
      setContratti(contrattiRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Punto Vendita handlers
  const handlePuntoSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...puntoForm, cliente_id: id };
      // Convert empty strings to null for optional email fields (Pydantic EmailStr validation)
      if (data.referente_email === '') data.referente_email = null;
      if (editingPunto) {
        await axios.put(`${API}/punti-vendita/${editingPunto.id}`, data);
        toast.success('Punto vendita aggiornato');
      } else {
        await axios.post(`${API}/punti-vendita`, data);
        toast.success('Punto vendita creato');
      }
      setPuntoDialogOpen(false);
      resetPuntoForm();
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore'));
    }
  };

  const handleEditPunto = (punto) => {
    setEditingPunto(punto);
    setPuntoForm({
      nome: punto.nome || '',
      indirizzo: punto.indirizzo || '',
      citta: punto.citta || '',
      cap: punto.cap || '',
      provincia: punto.provincia || '',
      referente_nome: punto.referente_nome || '',
      referente_telefono: punto.referente_telefono || '',
      referente_email: punto.referente_email || '',
      note: punto.note || ''
    });
    setPuntoDialogOpen(true);
  };

  const handleDeletePunto = async (puntoId) => {
    if (!window.confirm('Eliminare questo punto vendita?')) return;
    try {
      await axios.delete(`${API}/punti-vendita/${puntoId}`);
      toast.success('Punto vendita eliminato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore'));
    }
  };

  const resetPuntoForm = () => {
    setEditingPunto(null);
    setPuntoForm({
      nome: '',
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
      referente_nome: '',
      referente_telefono: '',
      referente_email: '',
      note: ''
    });
  };

  // Impianto handlers
  const handleImpiantoSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...impiantoForm, cliente_id: id };
      if (!data.punto_vendita_id) delete data.punto_vendita_id;
      let createdImpianto = null;
      if (editingImpianto) {
        await axios.put(`${API}/impianti/${editingImpianto.id}`, data);
        toast.success('Impianto aggiornato');
      } else {
        const res = await axios.post(`${API}/impianti`, data);
        createdImpianto = res.data;
        toast.success('Impianto creato');
      }

      // Se abbiamo un articolo scansionato in magazzino, lo assegniamo automaticamente
      if (scannedArticolo && !editingImpianto && createdImpianto) {
        try {
          await axios.post(`${API}/magazzino/articoli/${scannedArticolo.id}/assegna`, {
            cliente_id: id,
            impianto_id: createdImpianto.id,
            note: `Assegnato tramite impianto ${createdImpianto.matricola || ''}`,
          });
          toast.success(`📦 Articolo "${scannedArticolo.descrizione}" scaricato dal magazzino`);
        } catch (assignErr) {
          toast.warning(`Impianto creato, ma non ho potuto scaricare dal magazzino: ${parseApiError(assignErr, 'errore assegnazione')}`);
        }
      }

      setImpiantoDialogOpen(false);
      resetImpiantoForm();
      setScannedArticolo(null);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore'));
    }
  };

  const handleEditImpianto = (impianto) => {
    setEditingImpianto(impianto);
    setImpiantoForm({
      punto_vendita_id: impianto.punto_vendita_id || '',
      tipo: impianto.tipo || '',
      marca: impianto.marca || '',
      modello: impianto.modello || '',
      matricola: impianto.matricola || '',
      data_installazione: impianto.data_installazione || '',
      data_scadenza_verifica: impianto.data_scadenza_verifica || '',
      note: impianto.note || ''
    });
    setImpiantoDialogOpen(true);
  };

  const handleDeleteImpianto = async (impiantoId) => {
    if (!window.confirm('Eliminare questo impianto?')) return;
    try {
      await axios.delete(`${API}/impianti/${impiantoId}`);
      toast.success('Impianto eliminato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore'));
    }
  };

  const resetImpiantoForm = () => {
    setEditingImpianto(null);
    setImpiantoForm({
      punto_vendita_id: '',
      tipo: '',
      marca: '',
      modello: '',
      matricola: '',
      data_installazione: '',
      data_scadenza_verifica: '',
      note: ''
    });
  };

  const getPuntoVenditaName = (puntoId) => {
    const punto = puntiVendita.find(p => p.id === puntoId);
    return punto ? punto.nome : 'Non assegnato';
  };

  const getImpiantiByPunto = (puntoId) => {
    return impianti.filter(i => i.punto_vendita_id === puntoId);
  };

  const getImpiantiSenzaPunto = () => {
    return impianti.filter(i => !i.punto_vendita_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Cliente non trovato</p>
        <Link to="/clienti">
          <Button variant="outline" className="mt-4 rounded-none">Torna ai clienti</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cliente-detail">
      <div className="flex items-center gap-4">
        <Link to="/clienti">
          <Button variant="ghost" size="icon" className="rounded-none">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-black tracking-tight">{cliente.ragione_sociale}</h1>
          <p className="text-muted-foreground text-sm">
            {cliente.codice && <span className="font-mono font-bold text-cyan-600" data-testid="cliente-codice">{cliente.codice} | </span>}
            {cliente.partita_iva && `P.IVA: ${cliente.partita_iva}`}
            {cliente.sdi && ` | SDI: ${cliente.sdi}`}
          </p>
        </div>
      </div>

      {/* Info Card - Redesigned for Mobile */}
      <Card className="rounded-md border-l-4 border-l-cyan-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Building2 className="h-4 w-4 text-cyan-500" />
            Dati Anagrafici
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            {/* Sede Legale */}
            <div className="p-3 bg-muted/50 rounded-md">
              <p className="text-muted-foreground text-xs font-mono uppercase mb-2 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Sede Legale
              </p>
              <p className="font-medium">{cliente.indirizzo_legale || '-'}</p>
              <p className="text-muted-foreground">{cliente.cap_legale} {cliente.citta_legale} {cliente.provincia_legale}</p>
            </div>
            {/* Contatti */}
            <div className="p-3 bg-muted/50 rounded-md">
              <p className="text-muted-foreground text-xs font-mono uppercase mb-2">Contatti</p>
              {cliente.telefono && (
                <a href={`tel:${cliente.telefono}`} className="flex items-center gap-2 text-cyan-600 hover:underline mb-1">
                  <Phone className="h-3 w-3" /> {cliente.telefono}
                </a>
              )}
              {cliente.email && (
                <a href={`mailto:${cliente.email}`} className="flex items-center gap-2 text-cyan-600 hover:underline">
                  <Mail className="h-3 w-3" /> {cliente.email}
                </a>
              )}
              {cliente.pec && (
                <a href={`mailto:${cliente.pec}`} className="flex items-center gap-2 text-cyan-600 hover:underline mt-1" data-testid="cliente-pec-display">
                  <Mail className="h-3 w-3" /> <span className="font-mono text-[10px] uppercase text-muted-foreground mr-1">PEC</span> {cliente.pec}
                </a>
              )}
              {!cliente.telefono && !cliente.email && !cliente.pec && <span className="text-muted-foreground">-</span>}
            </div>
            {/* Dati Fiscali */}
            <div className="p-3 bg-muted/50 rounded-md">
              <p className="text-muted-foreground text-xs font-mono uppercase mb-2">Dati Fiscali</p>
              <p className="font-mono text-xs">C.F.: {cliente.codice_fiscale || '-'}</p>
              <p className="font-mono text-xs">SDI: {cliente.sdi || '-'}</p>
            </div>
          </div>
          {cliente.note && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-muted-foreground text-xs font-mono uppercase mb-1">Note</p>
              <p className="text-sm bg-amber-500/10 p-2 rounded border-l-2 border-amber-500">{cliente.note}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline Attività */}
      <ClientTimeline clienteId={id} clienteName={cliente.ragione_sociale} />

      {/* Punti Vendita Section */}
      <Card className="rounded-none">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Punti Vendita ({puntiVendita.length})
          </CardTitle>
          {isAdmin() && (
            <Dialog open={puntoDialogOpen} onOpenChange={(open) => { setPuntoDialogOpen(open); if (!open) resetPuntoForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-none font-mono uppercase text-xs" data-testid="new-punto-btn">
                  <Plus className="h-3 w-3 mr-1" />
                  Nuovo Punto
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl rounded-none">
                <DialogHeader>
                  <DialogTitle className="font-mono uppercase tracking-wider">
                    {editingPunto ? 'Modifica Punto Vendita' : 'Nuovo Punto Vendita'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePuntoSubmit} className="space-y-4">
                  <div>
                    <Label className="text-xs font-mono uppercase">Nome Punto Vendita *</Label>
                    <Input
                      value={puntoForm.nome}
                      onChange={(e) => setPuntoForm({...puntoForm, nome: e.target.value})}
                      placeholder="Es: Negozio Centro, Ristorante Via Roma"
                    className="rounded-none"
                    required
                    data-testid="punto-nome"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-xs font-mono uppercase">Indirizzo *</Label>
                    <Input
                      value={puntoForm.indirizzo}
                      onChange={(e) => setPuntoForm({...puntoForm, indirizzo: e.target.value})}
                      className="rounded-none"
                      required
                      data-testid="punto-indirizzo"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase">Città *</Label>
                    <Input
                      value={puntoForm.citta}
                      onChange={(e) => setPuntoForm({...puntoForm, citta: e.target.value})}
                      className="rounded-none"
                      required
                      data-testid="punto-citta"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs font-mono uppercase">CAP</Label>
                      <Input
                        value={puntoForm.cap}
                        onChange={(e) => setPuntoForm({...puntoForm, cap: e.target.value})}
                        className="rounded-none"
                        data-testid="punto-cap"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase">Prov.</Label>
                      <Input
                        value={puntoForm.provincia}
                        onChange={(e) => setPuntoForm({...puntoForm, provincia: e.target.value})}
                        className="rounded-none"
                        data-testid="punto-provincia"
                      />
                    </div>
                  </div>
                </div>
                <div className="border-t border-border pt-4">
                  <p className="text-xs font-mono uppercase text-muted-foreground mb-3">Referente</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-mono uppercase">Nome</Label>
                      <Input
                        value={puntoForm.referente_nome}
                        onChange={(e) => setPuntoForm({...puntoForm, referente_nome: e.target.value})}
                        className="rounded-none"
                        data-testid="punto-referente-nome"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase">Telefono</Label>
                      <Input
                        value={puntoForm.referente_telefono}
                        onChange={(e) => setPuntoForm({...puntoForm, referente_telefono: e.target.value})}
                        className="rounded-none"
                        data-testid="punto-referente-telefono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-mono uppercase">Email</Label>
                      <Input
                        type="email"
                        value={puntoForm.referente_email}
                        onChange={(e) => setPuntoForm({...puntoForm, referente_email: e.target.value})}
                        className="rounded-none"
                        data-testid="punto-referente-email"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Note</Label>
                  <Textarea
                    value={puntoForm.note}
                    onChange={(e) => setPuntoForm({...puntoForm, note: e.target.value})}
                    className="rounded-none"
                    data-testid="punto-note"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setPuntoDialogOpen(false)} className="rounded-none">
                    Annulla
                  </Button>
                  <Button type="submit" className="rounded-none font-mono uppercase" data-testid="save-punto-btn">
                    {editingPunto ? 'Aggiorna' : 'Crea'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {puntiVendita.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun punto vendita registrato</p>
          ) : (
            <div className="space-y-4">
              {puntiVendita.map((punto) => (
                <div key={punto.id} className="border border-border p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-bold">{punto.nome}</h4>
                      <p className="text-sm text-muted-foreground">
                        {punto.indirizzo}, {punto.cap} {punto.citta} {punto.provincia}
                      </p>
                      {punto.referente_nome && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Ref: {punto.referente_nome} {punto.referente_telefono && `- ${punto.referente_telefono}`}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {isAdmin() && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none" onClick={() => handleEditPunto(punto)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none text-destructive" onClick={() => handleDeletePunto(punto.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Impianti del punto vendita */}
                  <div className="bg-muted p-3 mt-2">
                    <p className="text-xs font-mono uppercase text-muted-foreground mb-2">
                      Impianti ({getImpiantiByPunto(punto.id).length})
                    </p>
                    {getImpiantiByPunto(punto.id).length === 0 ? (
                      <p className="text-xs text-muted-foreground">Nessun impianto</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {getImpiantiByPunto(punto.id).map((imp) => (
                          <div key={imp.id} className="bg-background p-2 text-xs flex justify-between items-center">
                            <div>
                              <span className="font-mono">{imp.matricola}</span>
                              <span className="text-muted-foreground ml-2">{imp.tipo}</span>
                            </div>
                            <Badge variant="outline" className="rounded-none text-xs">
                              {formatDate(imp.data_scadenza_verifica)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Impianti senza punto vendita */}
          {getImpiantiSenzaPunto().length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs font-mono uppercase text-muted-foreground mb-2">
                Impianti non assegnati ({getImpiantiSenzaPunto().length})
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {getImpiantiSenzaPunto().map((imp) => (
                  <div key={imp.id} className="bg-muted p-2 text-xs flex justify-between items-center">
                    <div>
                      <span className="font-mono">{imp.matricola}</span>
                      <span className="text-muted-foreground ml-2">{imp.tipo}</span>
                    </div>
                    <Badge variant="outline" className="rounded-none text-xs">
                      {formatDate(imp.data_scadenza_verifica)}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for other data */}
      <Tabs defaultValue="impianti" className="w-full">
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger value="impianti" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            <Printer className="h-4 w-4 mr-2" />
            Tutti Impianti ({impianti.length})
          </TabsTrigger>
          <TabsTrigger value="abbonamenti" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            <CalendarClock className="h-4 w-4 mr-2" />
            Contratti ({contratti.length + abbonamenti.length})
          </TabsTrigger>
          <TabsTrigger value="tickets" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            <Ticket className="h-4 w-4 mr-2" />
            Tickets ({tickets.length})
          </TabsTrigger>
          <TabsTrigger value="documenti" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-6">
            <FileText className="h-4 w-4 mr-2" />
            Documenti ({documenti.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="impianti" className="mt-4">
          <ImpiantiTab
            clienteId={id}
            impianti={impianti}
            puntiVendita={puntiVendita}
            isAdmin={isAdmin()}
            getPuntoVenditaName={getPuntoVenditaName}
            dialogOpen={impiantoDialogOpen}
            onDialogOpenChange={(open) => { setImpiantoDialogOpen(open); if (!open) resetImpiantoForm(); }}
            editingImpianto={editingImpianto}
            form={impiantoForm}
            setForm={setImpiantoForm}
            scannedArticolo={scannedArticolo}
            onScanClick={() => setImpiantoScannerOpen(true)}
            onSubmit={handleImpiantoSubmit}
            onEdit={handleEditImpianto}
            onDelete={handleDeleteImpianto}
            onQrAde={openQrAde}
            onSostituisci={openSostituisci}
          />
        </TabsContent>

        <TabsContent value="abbonamenti" className="mt-4">
          <AbbonamentiTab clienteId={id} contratti={contratti} abbonamenti={abbonamenti} isAdmin={isAdmin()} />
        </TabsContent>

        <TabsContent value="tickets" className="mt-4">
          <TicketsTab tickets={tickets} />
        </TabsContent>

        <TabsContent value="documenti" className="mt-4">
          <DocumentiTab documenti={documenti} />
        </TabsContent>
      </Tabs>

      {/* Scanner impianto → magazzino */}
      <BarcodeScannerModal
        open={impiantoScannerOpen}
        onOpenChange={setImpiantoScannerOpen}
        onScan={handleImpiantoScan}
        title="Scansiona matricola cassa"
      />

      {/* Sostituzione cassa */}
      <SostituisciCassaDialog
        open={sostituisciOpen}
        onOpenChange={setSostituisciOpen}
        impianto={impiantoDaSostituire}
        onDone={(data) => {
          fetchData();
          // Il QR AdE era della vecchia cassa: apri subito il dialog per scansionare quello nuovo
          if (data?.impianto) {
            toast.info('📷 Ricordati di scansionare il QR AdE della NUOVA cassa — te lo apro subito', { duration: 7000 });
            setImpiantoQr(data.impianto);
            setQrAdeOpen(true);
          }
        }}
      />

      {/* QR Agenzia delle Entrate */}
      <QrAdeDialog
        open={qrAdeOpen}
        onOpenChange={setQrAdeOpen}
        impianto={impiantoQr}
        onSaved={() => fetchData()}
      />
    </div>
  );
}
